<template>
  <div class="p-1" data-testid="retroactive-suggestion">
    <!-- ============================================================
         DEPOIS DE LANÇAR — o lote criado
         ============================================================ -->
    <template v-if="result">
      <div class="d-flex align-items-center mb-1">
        <feather-icon icon="CheckIcon" size="18" class="text-success mr-50" />
        <h5 class="mb-0">{{ resultTitle }}</h5>
      </div>

      <!--
        A referência do lote é o que liga esta tela à de Ajustes: é por ela que o Financeiro acha
        o lançamento depois. Sem mostrá-la aqui, o rastro existe mas ninguém sabe o número.
      -->
      <b-card v-if="result.batch_reference" no-body class="border mb-1">
        <div class="p-1">
          <div class="d-flex justify-content-between align-items-center">
            <strong class="text-primary">{{ result.batch_reference }}</strong>
            <b-badge variant="light-success">Aplicado</b-badge>
          </div>
          <small class="text-muted">
            Ajuste retroativo de spread do contrato {{ contractId }}
          </small>
        </div>
      </b-card>

      <template v-if="result.created && result.created.length">
        <h6 class="text-uppercase text-muted mb-50 retroactive-label">Lançados</h6>
        <div
          v-for="item in result.created"
          :key="'ok-' + item.consultant_id"
          class="d-flex justify-content-between align-items-center py-50 border-bottom"
        >
          <span>{{ item.consultant_name || "consultor " + item.consultant_id }}</span>
          <div>
            <span :class="item.credit_type === creditType ? 'text-success' : 'text-danger'">
              {{ item.credit_type === creditType ? "+" : "−" }} {{ item.amount | toCurrency }}
            </span>
            <b-badge variant="light-warning" class="ml-50">Pendente</b-badge>
          </div>
        </div>
      </template>

      <!--
        `skipped[]` com o motivo, e não silêncio. O backend já redige a razão de cada linha pulada;
        escondê-la faria o Financeiro conferir na mão por que faltou alguém.
      -->
      <template v-if="result.skipped && result.skipped.length">
        <h6 class="text-uppercase text-muted mb-50 mt-1 retroactive-label">Não lançados</h6>
        <div v-for="item in result.skipped" :key="'skip-' + item.consultant_id" class="mb-25">
          <small>
            <strong>{{ item.consultant_name || "consultor " + item.consultant_id }}</strong>
            — {{ item.reason }}
          </small>
        </div>
      </template>

      <b-alert variant="secondary" show class="mt-1 mb-0">
        <div class="alert-body">
          <small>
            Os lançamentos entram na fila de aprovação da tela de Ajustes, com a referência do lote
            na linha. <strong>Quem alterou o spread não aprova o próprio lançamento.</strong>
          </small>
        </div>
      </b-alert>

      <div class="d-flex mt-2">
        <b-button variant="primary" @click="$emit('close')">Fechar</b-button>
      </div>
    </template>

    <!-- ============================================================
         ANTES DE LANÇAR — a sugestão
         ============================================================ -->
    <template v-else>
      <div class="d-flex align-items-center mb-1">
        <feather-icon icon="RotateCcwIcon" size="18" class="text-primary mr-50" />
        <h5 class="mb-0">Parcelas já faturadas ficaram com o percentual antigo</h5>
      </div>

      <p class="mb-1">
        O recálculo do contrato só alcança as parcelas ainda não faturadas. As
        <strong>{{ preview.last_paid_installment }}</strong> primeiras já foram, e continuam com a
        distribuição anterior.
      </p>

      <!--
        ⚠️ Os avisos saem VERBATIM do backend, que já os redige para o Financeiro (soma abaixo de
        100, beneficiário que saiu da distribuição, competência não identificada). Reescrevê-los
        aqui duplicaria a regra em dois lugares que envelhecem separados.
      -->
      <b-alert
        v-for="(warning, index) in preview.warnings"
        :key="'w-' + index"
        variant="warning"
        show
        class="mb-1"
      >
        <div class="alert-body">
          <small>{{ warning }}</small>
        </div>
      </b-alert>

      <!--
        ============ 93% DOS CASOS: UM BENEFICIÁRIO ============
        Medido no banco: 13.411 de 14.422 alterações de spread mexem em uma pessoa só. Uma tabela
        com seleção para um nome seria cerimônia — a tela diz o número e sai da frente.
      -->
      <b-card v-if="eligibleLines.length === 1" no-body class="border mb-1">
        <div class="p-1 d-flex justify-content-between align-items-center">
          <div>
            <strong>
              {{ singleLine.consultant_name || "consultor " + singleLine.consultant_id }}
            </strong>
            <div>
              <small class="text-muted">
                {{ percentage(singleLine.previous_percentage) }} →
                {{ percentage(singleLine.new_percentage) }}
                <template v-if="preview.reference_competence">
                  · desde {{ preview.reference_competence }}
                </template>
              </small>
            </div>
          </div>
          <h5 :class="['mb-0', singleLine.difference > 0 ? 'text-success' : 'text-danger']">
            {{ singleLine.difference > 0 ? "+" : "−" }}
            {{ absolute(singleLine.difference) | toCurrency }}
          </h5>
        </div>
      </b-card>

      <!-- ============ OS OUTROS 7%: SELEÇÃO ============ -->
      <template v-else>
        <div
          v-for="line in eligibleLines"
          :key="line.consultant_id"
          class="d-flex justify-content-between align-items-center py-50 border-bottom"
        >
          <b-form-checkbox v-model="selected" :value="line.consultant_id" class="mr-1">
            <span>{{ line.consultant_name || "consultor " + line.consultant_id }}</span>
            <div>
              <small class="text-muted">
                {{ line.role_name }} · {{ percentage(line.previous_percentage) }} →
                {{ percentage(line.new_percentage) }}
              </small>
            </div>
          </b-form-checkbox>
          <span :class="line.difference > 0 ? 'text-success' : 'text-danger'">
            {{ line.difference > 0 ? "+" : "−" }} {{ absolute(line.difference) | toCurrency }}
          </span>
        </div>

        <!--
          A Empresa aparece por último e SEM seleção. Ela entra na memória de cálculo porque é o
          resíduo que faz a soma fechar em zero, mas não vira lançamento: quem paga não cobra de si
          mesma. Omiti-la faria a conta da tela não bater com a do backend.
        -->
        <div
          v-if="companyLine"
          class="d-flex justify-content-between align-items-center py-50 border-bottom text-muted"
        >
          <div>
            <span>{{ companyLine.consultant_name || "Empresa" }}</span>
            <div><small>Não recebe ajuste: é o contrapeso contábil da conta.</small></div>
          </div>
          <!--
            ⚠️ COM sinal, como todas as outras linhas. Esta é justamente a linha que faz a conta
            fechar: sem o sinal, quem lê não consegue verificar se a soma da tela bate, e um
            resíduo cedido pela Empresa fica idêntico a um resíduo absorvido por ela.
          -->
          <span>
            {{ companyLine.difference > 0 ? "+" : "−" }}
            {{ absolute(companyLine.difference) | toCurrency }}
          </span>
        </div>

        <!--
          ⚠️ O líquido DA SELEÇÃO, não o total. O `net_effect` do preview fecha em zero por
          construção; o da seleção só fecha se o conjunto inteiro estiver marcado. A diferença,
          quando existe, sai do resultado da Futuro — exibir um zero aqui seria entregar uma
          correção parcial com aparência de completa.
        -->
        <b-alert :variant="selectionIsComplete ? 'secondary' : 'warning'" show class="mt-1 mb-1">
          <div class="alert-body d-flex justify-content-between align-items-center">
            <span>Líquido da seleção</span>
            <strong>{{ selectedNetEffect | toCurrency }}</strong>
          </div>
          <div v-if="!selectionIsComplete" class="alert-body pt-0">
            <small>
              Deixando {{ unselectedNames }} de fora, a diferença fica com a Futuro em vez de se
              anular entre os beneficiários.
            </small>
          </div>
        </b-alert>
      </template>

      <!--
        ⚠️ NÃO existe opção de parcelar, e a ausência é deliberada.
        `recurrence_in_months` no módulo não reparte um total: `FinancialAdjustmentService::create`
        repete o valor CHEIO N vezes, uma linha por mês, cada uma cobrada por inteiro. Como a
        correção retroativa já é o total devido sobre todas as parcelas faturadas, oferecer "12x"
        aqui lançaria doze vezes a dívida — R$ 1.079,28 viram R$ 12.951,36 num contrato real.
        Medido também: 100% das correções de spread feitas hoje são de parcela única.
      -->
      <div class="d-flex">
        <b-button
          variant="primary"
          class="mr-1"
          :disabled="launching || selected.length === 0"
          @click="launch"
        >
          {{ launching ? "Lançando..." : launchLabel }}
        </b-button>
        <b-button variant="outline-secondary" :disabled="launching" @click="$emit('close')">
          Agora não
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import { BAlert, BBadge, BButton, BCard, BFormCheckbox } from "bootstrap-vue";
import { mapActions } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { CREDIT } from "@/constants/credit_types";
import * as types from "../store/types";

/**
 * A sugestão de ajuste retroativo, depois que o spread foi salvo.
 *
 * O recálculo do contrato só refaz as comissões das parcelas AINDA NÃO faturadas; as que já foram
 * ficam com o percentual antigo, e é isso que o Financeiro corrige na mão hoje.
 *
 * ⚠️ `preview` é memória de cálculo do backend e não se recalcula aqui. A tela só decide QUEM
 * entra — percentual, valor e número de parcelas (sempre 1) vêm prontos do backend.
 */
export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BFormCheckbox,
  },
  props: {
    preview: {
      type: Object,
      required: true,
    },
    contractId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      selected: [],
      launching: false,
      // ⚠️ `credit_type` é STRING no domínio ("credit"/"debt"), não 1/0. Comparar com número
      // pintaria todo lançamento de vermelho e trocaria o sinal de quem tem a receber.
      creditType: CREDIT,
      result: null,
    };
  },
  computed: {
    eligibleLines: function () {
      return (this.preview.lines || []).filter((line) => line.eligible_for_adjustment);
    },
    singleLine: function () {
      return this.eligibleLines[0] || {};
    },
    companyLine: function () {
      return (this.preview.lines || []).find((line) => line.is_company);
    },
    selectionIsComplete: function () {
      return this.selected.length === this.eligibleLines.length;
    },
    selectedNetEffect: function () {
      // ⚠️ Soma os valores ARREDONDADOS — que são os que a tela imprime linha a linha e os que o
      // backend efetivamente lança. Somando `difference` cru, o total exibido não era a soma das
      // linhas logo acima dele, e quem conferisse na mão encontraria um centavo sem explicação.
      return this.eligibleLines
        .filter((line) => this.selected.includes(line.consultant_id))
        .reduce((total, line) => total + this.roundedSigned(line.difference), 0);
    },
    unselectedNames: function () {
      return this.eligibleLines
        .filter((line) => !this.selected.includes(line.consultant_id))
        .map((line) => line.consultant_name || "consultor " + line.consultant_id)
        .join(", ");
    },
    launchLabel: function () {
      if (this.eligibleLines.length === 1) {
        return "Lançar o ajuste";
      }

      return this.selectionIsComplete
        ? "Lançar os " + this.selected.length + " ajustes"
        : "Lançar " + this.selected.length + " de " + this.eligibleLines.length;
    },
    resultTitle: function () {
      const total = (this.result.created || []).length;

      return total === 1 ? "1 ajuste lançado." : total + " ajustes lançados.";
    },
  },
  created() {
    this.selected = this.eligibleLines.map((line) => line.consultant_id);
  },
  methods: {
    ...mapActions({
      createRetroactiveAdjustments: types.CREATE_RETROACTIVE_SPREAD_ADJUSTMENTS,
    }),
    percentage(value) {
      return Number(value || 0)
        .toFixed(2)
        .replace(".", ",") + "%";
    },
    absolute(value) {
      return Math.abs(Number(value) || 0);
    },
    /** Ao centavo, preservando o sinal: espelha `roundedSigned()` do serviço. */
    roundedSigned(value) {
      const numero = Number(value) || 0;
      const arredondado = Math.round(Math.abs(numero) * 100) / 100;

      return numero < 0 ? -arredondado : arredondado;
    },
    launch() {
      this.launching = true;

      const body = {};

      // Só manda a seleção quando ela é PARCIAL: ausente significa "tudo o que é elegível", e quem
      // decide o que é elegível é o backend — repetir esse critério aqui abriria divergência.
      if (!this.selectionIsComplete) {
        body.consultant_ids = this.selected;
      }

      this.createRetroactiveAdjustments({ contractId: this.contractId, body })
        .then((response) => {
          this.result = response.data;
          this.$emit("launched", response.data);
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text:
                "Não foi possível lançar o ajuste retroativo. O spread já está salvo — o lançamento pode ser feito pela tela de Ajustes.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.launching = false;
        });
    },
  },
};
</script>

<style scoped>
.retroactive-label {
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}
</style>
